import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { updateData } from "../utils/firestoreService";
import { ChevronDown, SquareArrowOutUpRight, ClipboardCopy, RefreshCw } from "lucide-react";
import debounce from "lodash/debounce";
import { mostRecentDocument } from "../utils/historyProcessing";
import { useSidebar } from "../contexts/SidebarContext";
import { useThemeMode } from "../contexts/ThemeContext";
import Tooltip from "@mui/material/Tooltip";
import { DEBOUNCE_SAVE_MS, AUTOSAVE_INTERVAL_MS, COPY_FEEDBACK_MS } from "../constants/documentEditorConstants";
import EditorToolbar from "./EditorToolbar";
import SectionCommentButton from "../components/document/SectionCommentButton";
import CommentHighlightMark from "./extensions/CommentHighlightMark";
import { useTextSelection } from "../hooks/useTextSelection";
import SelectionTooltip from "../components/document/SelectionTooltip";
import CommentHighlightPopover from "../components/document/CommentHighlightPopover";

const EditableTextSection = ({
  section,
  sectionKey,
  documentData,
  setDocumentData,
  dataHistory,
  copyToClipboard,
  ClipboardCopy,
  patientId,
  docId,
  userSettings,
  defaultOpen = true,
  isEditable = true,
  isProcessing = false,
  sectionStatus = "pending", // 'pending', 'processing', 'completed'
  onSectionView,             // Callback when section is opened (for approval review tracking)
  requiresReview = false,    // Whether this section requires review for approval
  isReviewed = false,        // Whether this section has been reviewed
  onReprocessSection,        // Callback to reprocess this section (AppShell v3 only)
  onAddSectionComment,       // Callback to open comment margin for this section
  onAddTextComment,          // Callback to add a text-anchored comment (Phase 7)
  sectionComments = [],      // Comments for this section (for applying highlights)
  onSyncAnchors,             // Callback to batch-update anchor positions on save (Phase 7)
  onPromoteOrphanedComment,  // Callback to promote orphaned anchored comment to section-level
}) => {
  const [editorContent, setEditorContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isHistory, setIsHistory] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [lastSavedContent, setLastSavedContent] = useState(""); // Initialize to empty string
  const hasBeenEdited = useRef(false); // Track if user has genuinely edited (not just editor init)
  const isEditableRef = useRef(isEditable);
  isEditableRef.current = isEditable;
  const hasToggledAccordion = useRef(false);
  const [copiedIcon, setCopiedIcon] = useState(false);
  const focusTimeoutRef = useRef(null); // Prevent rapid focus/blur
  const [excludeExport, setExcludeExport] = useState(() => {
    const value = documentData?.settings?.[section.dataField]?.excludeFromExport;
    return value !== undefined ? Boolean(value) : true;
  });

  // Get the global setIsActivelyEditing function from context
  const { setIsActivelyEditing } = useSidebar();

  // Sync local editing state with global context
  useEffect(() => {
    const editorId = `text-${section.dataField || section.title}`;
    setIsActivelyEditing(editorId, isEditing && isEditable);

    // Cleanup on unmount
    return () => {
      setIsActivelyEditing(editorId, false);
    };
  }, [isEditing, isEditable, setIsActivelyEditing, section.dataField, section.title]);

  // Determine initial accordion state based on user preference and processing status
  const savedAccordionState = documentData?.settings?.[section.dataField]?.accordionOpen;
  const shouldRespectSavedState = userSettings?.collapsedSections !== false;

  let initialOpen;
  if (shouldRespectSavedState && savedAccordionState !== undefined) {
    initialOpen = savedAccordionState;
  } else if (shouldRespectSavedState) {
    // For processing documents, keep unprocessed sections closed
    initialOpen = sectionStatus === "completed" ? true : defaultOpen;
  } else {
    // If collapsedSections is false, always open unless processing and not complete
    initialOpen = sectionStatus === "pending" ? false : true;
  }

  const [isOpen, setIsOpen] = useState(initialOpen);

  // Fire section view tracking if section is initially open
  useEffect(() => {
    if (isEditable && isOpen && onSectionView && section.dataField) {
      onSectionView(section.dataField);
    }
    // Only run on mount — intentionally excluding isOpen from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open section when it completes during processing
  // Note: Only trigger on sectionStatus change, not isOpen change, to avoid re-opening after user closes
  useEffect(() => {
    if (sectionStatus === "completed") {
      const savedState = documentData?.settings?.[section.dataField]?.accordionOpen;
      // Only auto-open if user hasn't explicitly set a preference
      if (savedState === undefined && !hasToggledAccordion.current) {
        setIsOpen(true);
      }
    }
  }, [sectionStatus, documentData?.settings, section.dataField]);

  // Debounced save with memoization for stability
  const debouncedSave = useMemo(() => debounce((content) => handleSave(content), DEBOUNCE_SAVE_MS), []);

  // Cancel debounced save on unmount to prevent post-unmount saves
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, []);

  // Update editorContent and sync lastSavedContent on data changes
  // IMPORTANT: Skip sync if user is actively editing to prevent revision loss (race condition with polling)
  useEffect(() => {
    // Guard: Don't overwrite local state if user has unsaved edits
    if (isEditing || hasBeenEdited.current) {
      return;
    }

    let initialContent = "";
    if (section.dataField !== undefined && documentData && documentData[section.dataField]) {
      initialContent = documentData[section.dataField].summary || "";
    } else if (section.displayHistory !== undefined && dataHistory && dataHistory.length > 0) {
      const mostRecent = mostRecentDocument(dataHistory, section.dataField);
      if (mostRecent && mostRecent[section.dataField]) {
        initialContent = mostRecent[section.dataField].summary || "";
        setIsHistory(true);
      }
    } else if (documentData && documentData.defaultData) {
      initialContent = documentData.defaultData || "";
    }
    // Normalize literal "\n" escape sequences that slip through a JSON round-trip in the
    // text-generation pipeline. TipTap parses HTML, so convert to <br> for visible breaks.
    // See docs/updates/2026-04-20-library-transcript-newline-chars.md
    if (typeof initialContent === 'string' && initialContent.includes('\\n')) {
      initialContent = initialContent.replace(/\\n/g, '<br>');
    }
    setEditorContent(initialContent);
    setLastSavedContent(initialContent); // Sync to prevent false change detection
    hasBeenEdited.current = false; // Reset edit flag on data reload
  }, [section.displayHistory, section.dataField, dataHistory, documentData, isEditing]);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // sync exclude setting from documentData when it changes
    const value = documentData?.settings?.[section.dataField]?.excludeFromExport;
    setExcludeExport(value !== undefined ? Boolean(value) : true);
  }, [documentData, section.dataField]);

  const handleSave = async (content) => {
    // A queued autosave must respect permissions at the time it runs.
    if (!isEditableRef.current) return;
    // Normalize empty editor states
    const normalizedContent = (!content || content === "<p></p>") ? "" : content;

    // Loading, focusing or leaving a section does not count as an edit.
    if (!hasBeenEdited.current || normalizedContent === lastSavedContent) {
      return;
    }

    setIsSaving(true);
    try {
      await updateData(`patients/${patientId}/documents`, docId, {
        [`${section.dataField}.summary`]: normalizedContent,
      });
      setLastSavedContent(normalizedContent);

      // Anchor drift sync: scan ProseMirror doc for comment highlight marks
      if (onSyncAnchors && syncCommentAnchorsRef.current) {
        syncCommentAnchorsRef.current();
      }
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Ref for syncCommentAnchors — defined after useEditor to avoid TDZ
  const syncCommentAnchorsRef = useRef(null);

  const handleContentChange = (content) => {
    if (!isEditable) return;

    // Normalize empty state
    const normalizedContent = (!content || content === "<p></p>") ? "" : content;

    // Skip if this is an initial change (not a real user edit)
    if (!hasBeenEdited.current && normalizedContent === "") {
      return;
    }

    hasBeenEdited.current = true; // Mark as edited once user interacts
    setEditorContent(normalizedContent);
    setIsHistory(false);
    debouncedSave(normalizedContent);
  };

  const handleFocus = () => {
    if (!isEditable) return; // Prevent focus if not editable

    // Flush any pending debounced save to persist changes before editing resumes
    debouncedSave.flush();

    // Clear any pending blur timeout
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }

    // Only set to true if not already editing
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    if (!isEditable) return; // Prevent blur handling if not editable

    // Save immediately on blur
    handleSave(editorContent);
    setDocumentData((prevData) => ({
      ...prevData,
      [section.dataField]: {
        ...prevData[section.dataField],
        summary: editorContent,
      },
    }));
    setIsHistory(false);

    // Use a short timeout to prevent rapid blur/focus cycles
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }

    focusTimeoutRef.current = setTimeout(() => {
      // Only set editing to false if no refocus happened
      setIsEditing(false);
      focusTimeoutRef.current = null;
    }, 150);
  };

  const handleCopyClick = () => {
    copyToClipboard(editorContent, true);
    setCopiedIcon(true);
    setTimeout(() => setCopiedIcon(false), COPY_FEEDBACK_MS);
  };

  // Update auto-save to check for actual changes and edit flag
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (isEditing && !isSaving && editorContent !== lastSavedContent && hasBeenEdited.current) {
        handleSave(editorContent);
      }
    }, AUTOSAVE_INTERVAL_MS);

    return () => clearInterval(autoSaveInterval);
  }, [editorContent, isEditing, isSaving, lastSavedContent]);

  const handleExcludeToggle = async (e) => {
    // prevent bubble if inside header click areas
    e.stopPropagation();
    if (!isEditable) return;
    const newValue = !excludeExport;
    setExcludeExport(newValue);
    try {
      await updateData(`patients/${patientId}/documents`, docId, {
        [`settings.${section.dataField}.excludeFromExport`]: newValue,
      });
    } catch (err) {
      console.error("Error saving excludeExport setting:", err);
    }
  };

  const toggleAccordion = () => {
    const newOpen = !isOpen;
    hasToggledAccordion.current = true;
    setIsOpen(newOpen);
    // Readers can explore the note without changing the author's preferences.
    if (isEditable) {
      updateData(`patients/${patientId}/documents`, docId, {
        [`settings.${section.dataField}.accordionOpen`]: newOpen,
      }).catch(error => console.warn('Could not save section display preference:', error.message));
      // Track section view for approval review when opening
      if (newOpen && onSectionView && section.dataField) {
        onSectionView(section.dataField);
      }
    }
  };

  // Get status class for visual indicators
  const getStatusClass = () => {
    switch (sectionStatus) {
      case "completed":
        return "section-completed";
      case "processing":
        return "section-processing";
      case "pending":
        return "section-pending";
      default:
        return "";
    }
  };

  // Match the effective application theme, including company permission.
  const { isDarkMode } = useThemeMode();

  // TipTap editor instance
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      CommentHighlightMark,
    ],
    content: editorContent,
    editable: !isHistory && isEditable,
    onUpdate: ({ editor }) => {
      handleContentChange(editor.getHTML());
    },
    onFocus: () => handleFocus(),
    onBlur: () => handleBlur(),
  });

  // Sync editable state when props change
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isHistory && isEditable);
    }
  }, [editor, isHistory, isEditable]);

  // Extract current comment highlight mark positions from the ProseMirror doc
  const syncCommentAnchors = useCallback(() => {
    if (!editor || !onSyncAnchors) return;

    const doc = editor.state.doc;
    const markPositions = new Map(); // commentId → { from, to, text }

    doc.descendants((node, pos) => {
      if (!node.isText) return;
      const marks = node.marks.filter((m) => m.type.name === "commentHighlight" && m.attrs.commentId);
      marks.forEach((mark) => {
        const id = mark.attrs.commentId;
        const existing = markPositions.get(id);
        if (existing) {
          existing.to = pos + node.nodeSize;
        } else {
          markPositions.set(id, { from: pos, to: pos + node.nodeSize });
        }
      });
    });

    const updates = [];
    const orphanedIds = [];
    const anchoredComments = sectionComments.filter((c) => c.anchor && !c.deleted);

    anchoredComments.forEach((comment) => {
      const currentPos = markPositions.get(comment.id);
      if (!currentPos) {
        orphanedIds.push(comment.id);
        return;
      }
      const { from, to } = currentPos;
      const text = editor.state.doc.textBetween(from, to, " ").substring(0, 100);
      if (from !== comment.anchor.startOffset || to !== comment.anchor.endOffset) {
        updates.push({ commentId: comment.id, startOffset: from, endOffset: to, textSnippet: text });
      }
    });

    if (updates.length) onSyncAnchors(updates);
    if (orphanedIds.length && onPromoteOrphanedComment) {
      orphanedIds.forEach((id) => onPromoteOrphanedComment(id));
    }
  }, [editor, sectionComments, onSyncAnchors, onPromoteOrphanedComment]);

  // Keep ref in sync so handleSave (defined before useEditor) can call it
  useEffect(() => {
    syncCommentAnchorsRef.current = syncCommentAnchors;
  }, [syncCommentAnchors]);

  // Sync content from external sources (Firestore) into the editor
  useEffect(() => {
    if (editor && !isEditing && !hasBeenEdited.current) {
      const currentHTML = editor.getHTML();
      // Avoid feedback loop: only update if content actually differs
      if (editorContent && editorContent !== currentHTML) {
        editor.commands.setContent(editorContent, { emitUpdate: false });
      } else if (!editorContent && currentHTML !== "<p></p>") {
        editor.commands.setContent("", { emitUpdate: false });
      }
    }
  }, [editor, editorContent, isEditing]);

  // --- Text selection for inline comments (Phase 7) ---
  const { selection, clearSelection } = useTextSelection(onAddTextComment ? editor : null);

  // Apply comment highlight Marks when comments with anchors load
  // Includes load-time reconciliation: verifies text at stored offsets matches textSnippet,
  // falls back to text search if offsets drifted, skips if snippet not found (orphaned on next save).
  const appliedHighlightsRef = useRef(new Set());
  useEffect(() => {
    if (!editor || !sectionComments.length) return;

    const anchored = sectionComments.filter((c) => c.anchor && !c.deleted);
    if (!anchored.length) return;

    // Only apply marks we haven't applied yet in this editor session
    const newComments = anchored.filter((c) => !appliedHighlightsRef.current.has(c.id));
    if (!newComments.length) return;

    const doc = editor.state.doc;
    const docSize = doc.content.size;
    const fullText = doc.textBetween(0, docSize, " ");

    // Resolve anchor position: verify stored offsets, fall back to text search
    const resolveAnchorPosition = (anchor) => {
      const { startOffset, endOffset, textSnippet } = anchor;
      // 1. Try stored offsets if they're within bounds
      if (startOffset >= 0 && endOffset <= docSize && startOffset < endOffset) {
        const textAtOffset = doc.textBetween(startOffset, endOffset, " ");
        if (!textSnippet || textAtOffset === textSnippet) {
          return { from: startOffset, to: endOffset };
        }
      }
      // 2. Fall back to text search for the snippet in the doc
      if (textSnippet) {
        const idx = fullText.indexOf(textSnippet);
        if (idx !== -1) {
          // Convert plain text index to ProseMirror position
          // Walk the doc to find the ProseMirror offset corresponding to this text position
          let charCount = 0;
          let pmFrom = null;
          let pmTo = null;
          doc.descendants((node, pos) => {
            if (pmTo !== null) return false; // already found
            if (node.isText) {
              const nodeEnd = charCount + node.text.length;
              if (pmFrom === null && idx < nodeEnd) {
                pmFrom = pos + (idx - charCount);
              }
              const snippetEnd = idx + textSnippet.length;
              if (pmFrom !== null && snippetEnd <= nodeEnd) {
                pmTo = pos + (snippetEnd - charCount);
                return false;
              }
              charCount = nodeEnd;
            } else if (node.isBlock && charCount > 0) {
              charCount += 1; // block separator space from textBetween
            }
          });
          if (pmFrom !== null && pmTo !== null) {
            return { from: pmFrom, to: pmTo };
          }
        }
      }
      // 3. Snippet not found → skip (will be detected as orphaned on next save)
      return null;
    };

    editor.chain().focus(false);
    newComments.forEach((comment) => {
      const position = resolveAnchorPosition(comment.anchor);
      if (position) {
        editor.commands.setTextSelection(position);
        editor.commands.setMark("commentHighlight", { commentId: comment.id });
        appliedHighlightsRef.current.add(comment.id);
      }
    });
    // Clear selection after applying
    editor.commands.setTextSelection(0);
  }, [editor, sectionComments]);

  const handleAddTextCommentFromSelection = useCallback((sel) => {
    if (!onAddTextComment || !sel) return;
    onAddTextComment(section.dataField, section.title, {
      startOffset: sel.from,
      endOffset: sel.to,
      textSnippet: sel.text.substring(0, 100),
    });
    clearSelection();
  }, [onAddTextComment, section.dataField, section.title, clearSelection]);

  // Highlight popover for narrow screens (Phase 5)
  const [highlightPopover, setHighlightPopover] = useState(null);
  const hoverTimeoutRef = useRef(null);

  // Collect all overlapping comment IDs from nested highlight spans
  const collectHighlightComments = useCallback((target) => {
    const ids = new Set();
    let el = target;
    while (el) {
      if (el.classList?.contains("comment-highlight")) {
        const id = el.getAttribute("data-comment-id");
        if (id) ids.add(id);
      }
      el = el.parentElement;
    }
    return [...ids]
      .map((id) => sectionComments.find((c) => c.id === id))
      .filter(Boolean);
  }, [sectionComments]);

  // Click: show popover on narrow screens where margin zone is hidden
  const handleEditorClick = useCallback((e) => {
    if (window.innerWidth > 768) return;
    const target = e.target.closest(".comment-highlight");
    if (!target) { setHighlightPopover(null); return; }
    const comments = collectHighlightComments(target);
    if (!comments.length) return;
    const rect = target.getBoundingClientRect();
    setHighlightPopover({ comments, coords: { top: rect.top, bottom: rect.bottom, left: rect.left + rect.width / 2 } });
  }, [collectHighlightComments]);

  // Hover: show popover on wide screens
  const handleEditorMouseOver = useCallback((e) => {
    if (window.innerWidth <= 768) return;
    const target = e.target.closest(".comment-highlight");
    if (!target) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => setHighlightPopover(null), 200);
      return;
    }
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    const comments = collectHighlightComments(target);
    if (!comments.length) return;
    const rect = target.getBoundingClientRect();
    setHighlightPopover({ comments, coords: { top: rect.top, bottom: rect.bottom, left: rect.left + rect.width / 2 } });
  }, [collectHighlightComments]);

  const handleEditorMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setHighlightPopover(null), 200);
  }, []);

  return (
    <div className={`standard-container ${getStatusClass()}`} key={sectionKey}>
      <div className="edit-header-container">
        <div className="edit-header-left" onClick={toggleAccordion}
          role="button" tabIndex={0} aria-expanded={isOpen} aria-label={section.title}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleAccordion();
            }
          }}>
          <h3 className="edit-header">
            <Tooltip title={isOpen ? "Hide section" : "Show section"} arrow>
              <span className={`accordion-icon${isOpen ? " accordion-open" : ""}`}><ChevronDown size={16} /></span>
            </Tooltip>
            {section.title}
            {requiresReview && (
              <span
                className={`review-indicator ${isReviewed ? 'reviewed' : 'needs-review'}`}
                title={isReviewed ? 'Reviewed' : 'Requires review'}
              >
                {isReviewed ? '✓' : '⚠'}
              </span>
            )}
          </h3>
        </div>
        <div className="edit-header-right">
          <SectionCommentButton
            sectionKey={section.dataField}
            sectionTitle={section.title}
            onAddSectionComment={isEditable ? onAddSectionComment : undefined}
          />
          {isEditable && section.excludeExportIcon && SquareArrowOutUpRight && (
            <Tooltip title={excludeExport ? "Include in Export" : "Exclude from Export"} arrow>
              <span className={`export-exclude-icon${excludeExport ? " excluded" : ""}`} style={{ marginRight: "var(--space-2)", display: "inline-flex", alignItems: "center", cursor: "pointer" }} onClick={handleExcludeToggle} role="button" tabIndex={0} aria-label={excludeExport ? "Include in Export" : "Exclude from Export"} aria-pressed={excludeExport} onKeyDown={(e) => e.key === 'Enter' && handleExcludeToggle(e)}>
                {isOpen ? <SquareArrowOutUpRight size={14} /> : ""}
              </span>
            </Tooltip>
          )}
          {!section.excludeCopyIcon && (
            <Tooltip title="Copy to Clipboard" arrow>
              <span onClick={handleCopyClick} className={`copy-icon${copiedIcon ? " copied" : ""}`} role="button" tabIndex={0} aria-label="Copy section to clipboard" onKeyDown={(e) => e.key === 'Enter' && handleCopyClick()}>
                {isOpen ? <ClipboardCopy size={14} /> : ""}
              </span>
            </Tooltip>
          )}
          {/* Show reprocess icon for sections with AI steps that are empty or have errors */}
          {isEditable && isOpen && onReprocessSection && section.hasAiStep && (() => {
            // Get section data to check for error/failure state
            const sectionData = documentData?.[section.dataField];
            const hasError = Boolean(sectionData?.error);
            
            // Check if section content is empty
            // Handle: empty string, whitespace-only, editor empty states, undefined, null
            const normalizedContent = editorContent?.replace(/<[^>]*>/g, '').trim() || '';
            const isContentEmpty = !normalizedContent || (editor && editor.isEmpty);
            
            // Show reprocess if section has error OR is empty
            if (!hasError && !isContentEmpty) return null;
            
            return (
              <Tooltip title={hasError ? "Reprocess Section (failed)" : "Reprocess Section"} arrow>
                <span
                  onClick={() => {
                    if (sectionStatus !== "processing") {
                      onReprocessSection(section.dataField || section.stepId || section.id, section.title);
                    }
                  }}
                  className={`reprocess-icon${sectionStatus === "processing" ? " disabled" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-label="Reprocess section"
                  aria-disabled={sectionStatus === "processing"}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && sectionStatus !== "processing") {
                      onReprocessSection(section.dataField || section.stepId || section.id, section.title);
                    }
                  }}
                  style={{ 
                    marginLeft: "var(--space-2)", 
                    display: "inline-flex", 
                    alignItems: "center", 
                    cursor: sectionStatus === "processing" ? "not-allowed" : "pointer",
                    opacity: sectionStatus === "processing" ? 0.5 : 1
                  }}
                >
                  <RefreshCw size={14} />
                </span>
              </Tooltip>
            );
          })()}
        </div>
      </div>
      {isOpen && (
        <div className="text-editor-container" onClick={handleEditorClick} onMouseOver={handleEditorMouseOver} onMouseLeave={handleEditorMouseLeave}>
          <div
            className={`text-editor-box ${isHistory ? "text-history" : ""} ${!isEditable ? "text-readonly" : ""} ${isDarkMode ? "editor-dark-mode" : ""}`}
            style={{ resize: "vertical", overflow: "auto" }}
          >
            {isEditable && section.toolbarType !== "none" && editor && (
              <EditorToolbar editor={editor} toolbarType={section.toolbarType} />
            )}
            <EditorContent editor={editor} style={{ height: "96%" }} />
            {isEditable && onAddTextComment && (
              <SelectionTooltip
                selection={selection}
                onAddComment={handleAddTextCommentFromSelection}
              />
            )}
            {highlightPopover && (
              <CommentHighlightPopover
                comments={highlightPopover.comments}
                coords={highlightPopover.coords}
                onClose={() => setHighlightPopover(null)}
              />
            )}
          </div>
          <div className="editor-message">
            {section.message}
            {!isEditable && !isProcessing && <span>View only</span>}
            {!isEditable && isProcessing && sectionStatus === "pending" && <span className="processing-notice"> - Section will be editable when processing completes</span>}
            {!isEditable && isProcessing && sectionStatus === "processing" && <span className="processing-notice"> - Currently being processed...</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(EditableTextSection);
